import { paletteBase } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { GradientImageProps } from './gradient-bg.types';

/**
 * Solid background used for Budget and Budget Truck brands.
 * Reuses the shared `primaryDarkBlue` palette token (#00285F).
 */
const SOLID_BACKGROUND = paletteBase.primaryDarkBlue;

/**
 * Gradient background used for Avis (the default brand).
 * Composed from the hero-banner-specific palette tokens.
 */
const GRADIENT_BACKGROUND = `linear-gradient(0deg, ${paletteBase.heroBannerGradientStart} 20%, ${paletteBase.heroBannerGradientEnd} 100%)`;

/** Brands that use the flat solid color instead of the Avis gradient. */
const SOLID_BG_BRANDS = new Set(['budget', 'budgettruck']);

/**
 * GradientBg renders the decorative full-bleed background layer behind the
 * TopHeroBanner headline/content.
 *
 * Where it is used:
 * - Rendered exclusively by `TopHeroBanner`
 *   (libs/web-biz-components/lib/organisms/TopHeroBanner/TopHeroBanner.tsx),
 *   in the non-smile branch, as the fallback background when no hero image is
 *   configured.
 *
 * When it is shown:
 * - `TopHeroBanner` renders it when `hasGradientBg` is `true` (its default) and
 *   `showSmile` is falsy; otherwise a `ResponsiveImage` / `SmileBackground` is
 *   shown instead.
 *
 * Props:
 * - `smileType`: controls stacking order — `'backward'` places the background
 *   behind sibling content (`zIndex: 0`), any other value stacks it above
 *   (`zIndex: 5`).
 *
 * Background color is brand-driven: Budget / Budget Truck get a flat solid
 * color, while Avis gets the gradient.
 */
export const GradientBg: React.FC<GradientImageProps> = ({ smileType }) => {
  const { brand } = usePageContext();

  const useSolidBackground = SOLID_BG_BRANDS.has(brand);

  return (
    <div
      id="top-hero-banner-style"
      data-testid="gradient-bg-element"
      style={{
        borderRadius: 'inherit',
        zIndex: smileType === 'backward' ? 0 : 5,
        objectFit: 'cover',
        display: 'block',
        height: '100%',
        width: '100%',
        background: useSolidBackground ? SOLID_BACKGROUND : GRADIENT_BACKGROUND,
      }}
    />
  );
};
